import { initializeApp } from "firebase/app";
import { initializeAuth, getAuth, GoogleAuthProvider, browserLocalPersistence } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { Capacitor } from "@capacitor/core";

const isNative = Capacitor.isNativePlatform();

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    // Always use the default *.firebaseapp.com auth domain for now.
    // A custom domain (parentscheck.com) can be reintroduced here for web once the
    // domain is registered; native WKWebView auth must never use a custom domain.
    authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "parentschecks",
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "parentschecks.firebasestorage.app",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth with platform-appropriate persistence
// WKWebView (Capacitor) has issues with indexedDB, so use localStorage directly
let auth;
if (isNative) {
    auth = initializeAuth(app, {
        persistence: browserLocalPersistence,
    });
} else {
    auth = getAuth(app);
}

const googleProvider = new GoogleAuthProvider();
const db = getFirestore(app);
const storage = getStorage(app);

// Analytics & App Check — only in web browser, not in Capacitor native
let analytics = null;
let appCheck = null;

const platform = Capacitor.getPlatform();

if (!isNative) {
    // These APIs rely on browser-specific features (cookies, reCAPTCHA)
    import("firebase/analytics").then(({ getAnalytics }) => {
        analytics = getAnalytics(app);
    });

    import("firebase/app-check").then(({ initializeAppCheck, ReCaptchaV3Provider }) => {
        // Enable Debug Token for Localhost
        if (location.hostname === "localhost") {
            self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }

        // The reCAPTCHA v3 site key is project-specific; supply it via env once the
        // parentschecks key is registered in the Firebase console (App Check).
        // Skip App Check init when unset instead of failing with a foreign key.
        const recaptchaSiteKey = import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY;
        if (!recaptchaSiteKey) {
            console.warn('[AppCheck] VITE_APPCHECK_RECAPTCHA_SITE_KEY not set — App Check disabled for now.');
            return;
        }

        appCheck = initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(recaptchaSiteKey),
            isTokenAutoRefreshEnabled: true
        });
    });
} else if (platform === 'android') {
    // Native Android App Check (Play Integrity) via Capacitor bridge
    Promise.all([
        import("@capacitor-firebase/app-check"),
        import("firebase/app-check")
    ]).then(([{ FirebaseAppCheck }, { initializeAppCheck, CustomProvider }]) => {
        // Initialize the native App Check plugin
        FirebaseAppCheck.initialize({
            isTokenAutoRefreshEnabled: true
        }).catch(err => console.error("Native AppCheck init failed:", err));

        // Bridge native token generation to the Firebase JS SDK
        const customProvider = new CustomProvider({
            getToken: async () => {
                try {
                    const result = await FirebaseAppCheck.getToken({ forceRefresh: false });
                    return {
                        token: result.token,
                        expireTimeMillis: result.expireTimeMillis,
                    };
                } catch (err) {
                    console.error("Failed to get native App Check token:", err);
                    throw err;
                }
            },
        });

        appCheck = initializeAppCheck(app, {
            provider: customProvider,
            isTokenAutoRefreshEnabled: true,
        });
    }).catch(err => console.error("Firebase AppCheck Android setup Error:", err));
}

export { auth, googleProvider, db, storage, analytics, appCheck };
